import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Building2, Users, Activity, CalendarCheck, CreditCard, RefreshCw, Search, Download,
  X, ChevronRight, Monitor, Smartphone, UserX, Inbox, ShieldAlert,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import apiService from '../services/api';

// ─────────────────────────────────────────────────────────────
// Platform owner console — every customer company using Clock-out,
// who's on right now and when each person was last on.
// Backed by /api/platform (super admin only). LIVE only.
// ─────────────────────────────────────────────────────────────

const REFRESH_MS = 60 * 1000;

const PLAN = {
  trial:      { label: 'Trial',      bg: 'var(--warn-bg)', ink: 'var(--warn-text)', border: 'var(--warn-border)' },
  paying:     { label: 'Paying',     bg: 'var(--ok-bg)',   ink: 'var(--ok-text)',   border: 'var(--ok-border)' },
  enterprise: { label: 'Enterprise', bg: 'var(--tint)',    ink: 'var(--text)',      border: 'var(--border)' },
  manual:     { label: 'Free / manual', bg: 'var(--tint)', ink: 'var(--text)',      border: 'var(--border)' },
  read_only:  { label: 'Read only',  bg: 'var(--bad-bg)',  ink: 'var(--bad-text)',  border: 'var(--bad-border)' },
  none:       { label: 'No plan',    bg: 'var(--surface-2, var(--tint))', ink: 'var(--text-muted)', border: 'var(--border)' },
};

const FILTERS = [
  { key: 'all',       label: 'All' },
  { key: 'online',    label: 'On now' },
  { key: 'active7',   label: 'Active this week' },
  { key: 'quiet',     label: 'Quiet 30+ days' },
  { key: 'trial',     label: 'Trial' },
  { key: 'paying',    label: 'Paying' },
  { key: 'read_only', label: 'Read only' },
  { key: 'suspended', label: 'Suspended' },
];

const SORTS = [
  { key: 'active',  label: 'Last active' },
  { key: 'newest',  label: 'Newest' },
  { key: 'people',  label: 'Most people' },
  { key: 'name',    label: 'Name' },
];

const ROLE_LABEL = { super_admin: 'Super admin', admin: 'Admin', manager: 'Manager', member: 'Member' };

// ─── helpers ───
const unwrap = (r) => r?.data ?? r;
const toDate = (v) => (v ? (v instanceof Date ? v : new Date(v)) : null);

function ago(v, now = Date.now()) {
  const d = toDate(v);
  if (!d || isNaN(d)) return 'Never';
  const s = Math.max(0, Math.round((now - d.getTime()) / 1000));
  if (s < 60) return 'Just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const days = Math.round(h / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return format(d, d.getFullYear() === new Date(now).getFullYear() ? 'd MMM' : 'd MMM yyyy');
}

const fullDate = (v) => { const d = toDate(v); return d && !isNaN(d) ? format(d, 'd MMM yyyy, HH:mm') : '—'; };
const shortDate = (v) => { const d = toDate(v); return d && !isNaN(d) ? format(d, 'd MMM yyyy') : '—'; };
const dayLabel = (s) => { try { return format(parseISO(s), 'EEE d MMM'); } catch { return s; } };

function ClientIcon({ client, size = 13 }) {
  if (client === 'app') return <Smartphone size={size} aria-label="Mobile app" />;
  if (client === 'web') return <Monitor size={size} aria-label="Web" />;
  if (client === 'both') return <span style={{ display: 'inline-flex', gap: 2 }}><Monitor size={size} /><Smartphone size={size} /></span>;
  return null;
}
const clientLabel = (c) => ({ app: 'App', web: 'Web', both: 'Web + app', other: 'Other' }[c] || '');

function PlanChip({ billing }) {
  const plan = billing?.plan || 'none';
  const p = PLAN[plan] || PLAN.none;
  let text = p.label;
  if (plan === 'trial' && billing?.trialDaysLeft != null) text = `Trial · ${billing.trialDaysLeft}d left`;
  if (plan === 'paying' && billing?.warning === 'cancelling') text = 'Paying · cancelling';
  if (plan === 'paying' && billing?.warning === 'payment_failed') text = 'Payment failed';
  return <span className="pf-chip" style={{ background: p.bg, color: p.ink, borderColor: p.border }}>{text}</span>;
}

function initials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
}

function downloadCsv(filename, rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = rows.map((r) => r.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── Bars: one series, today darker; hover shows the detail ───
function DayBars({ data, height = 140, valueKey = 'people', detail }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...data.map((d) => d[valueKey] || 0));
  const last = data.length - 1;
  const h = hover != null ? data[hover] : null;
  return (
    <div>
      <div className="pf-bars" style={{ height }} onMouseLeave={() => setHover(null)} role="img"
        aria-label={`People active per day, last ${data.length} days`}>
        {data.map((d, i) => {
          const v = d[valueKey] || 0;
          const pct = v > 0 ? Math.max(4, (v / max) * 100) : 0;
          return (
            <div key={d.day} className="pf-bar-col" onMouseEnter={() => setHover(i)} title={`${dayLabel(d.day)}: ${v}`}>
              <div className="pf-bar-plot">
                {v > 0
                  ? <div className="pf-bar" style={{ height: `${pct}%`, background: i === last ? 'var(--chart-now)' : 'var(--chart-past)', opacity: hover != null && hover !== i ? 0.5 : 1 }} />
                  : <div className="pf-bar-zero" />}
              </div>
            </div>
          );
        })}
      </div>
      <div className="pf-bars-foot">
        <span>{data[0] ? dayLabel(data[0].day) : ''}</span>
        <span className="pf-bars-hover">{h ? (detail ? detail(h) : `${dayLabel(h.day)} · ${h[valueKey]}`) : 'Hover a day for detail'}</span>
        <span>Today</span>
      </div>
    </div>
  );
}

// ─── Company drawer ───
function CompanyDrawer({ companyId, onClose, now }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [q, setQ] = useState('');
  const closeRef = useRef(null);

  useEffect(() => {
    let live = true;
    setState({ loading: true, error: null, data: null });
    apiService.getPlatformCompany(companyId)
      .then((r) => { if (live) setState({ loading: false, error: null, data: unwrap(r) }); })
      .catch((e) => { if (live) setState({ loading: false, error: e?.response?.data?.message || e?.message || 'Could not load the company.', data: null }); });
    return () => { live = false; };
  }, [companyId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const d = state.data;
  const people = useMemo(() => {
    const list = d?.people || [];
    const term = q.trim().toLowerCase();
    if (!term) return list;
    return list.filter((p) => `${p.name} ${p.email} ${p.departments} ${p.role}`.toLowerCase().includes(term));
  }, [d, q]);

  const c = d?.company;
  const onlineCount = (d?.people || []).filter((p) => p.online).length;
  const activeCount = (d?.people || []).filter((p) => p.status === 'active').length;
  const neverCount = (d?.people || []).filter((p) => p.status === 'active' && !p.lastActiveAt && !p.lastLoginAt).length;

  const exportPeople = () => {
    if (!d) return;
    downloadCsv(`${(c.name || 'company').replace(/[^\w-]+/g, '-')}-people.csv`, [
      ['Name', 'Email', 'Role', 'Status', 'Departments', 'Last active', 'Via', 'Last sign-in', 'Days active (30)', 'Joined'],
      ...d.people.map((p) => [p.name, p.email, ROLE_LABEL[p.role] || p.role, p.status, p.departments, fullDate(p.lastActiveAt), clientLabel(p.lastActiveClient), fullDate(p.lastLoginAt), p.daysActive30, shortDate(p.createdAt)]),
    ]);
  };

  return createPortal(
    <div className="pf-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="pf-drawer" role="dialog" aria-modal="true" aria-label={c ? c.name : 'Company'}>
        <div className="pf-drawer-head">
          <div style={{ minWidth: 0, flex: 1 }}>
            <p className="pf-eyebrow">Company</p>
            <h2 className="pf-drawer-title">{c?.name || (state.loading ? 'Loading…' : 'Company')}</h2>
            {c && (
              <div className="pf-drawer-meta">
                <PlanChip billing={c.billing} />
                {!c.isActive && <span className="pf-chip pf-chip-bad">Suspended</span>}
                {c.isOwn && <span className="pf-chip">Your company</span>}
                <span>{c.country || '—'}{c.region ? ` · ${c.region}` : ''}</span>
                <span>Joined {shortDate(c.createdAt)}</span>
                {c.partner && <span>Partner: {c.partner.name}</span>}
              </div>
            )}
          </div>
          <button ref={closeRef} className="pf-icon-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        <div className="pf-drawer-body">
          {state.loading && (
            <div style={{ display: 'grid', gap: 10 }}>
              {[0, 1, 2, 3].map((i) => <span key={i} className="pf-skel" style={{ height: i === 0 ? 70 : 44 }} />)}
            </div>
          )}
          {state.error && <div className="pf-error"><ShieldAlert size={16} /> {state.error}</div>}

          {d && (
            <>
              <div className="pf-mini-stats">
                <div><b>{activeCount}</b><span>people</span></div>
                <div><b className={onlineCount ? 'pf-ok' : ''}>{onlineCount}</b><span>on now</span></div>
                <div><b>{d.requests.last30}</b><span>requests, 30 days</span></div>
                <div><b>{d.requests.pending}</b><span>waiting approval</span></div>
                <div><b>{d.pendingInvitations}</b><span>invites not accepted</span></div>
                <div><b className={neverCount ? 'pf-warn' : ''}>{neverCount}</b><span>never signed in</span></div>
              </div>

              {c.billing && c.billing.plan !== 'none' && (
                <p className="pf-note">
                  <CreditCard size={14} />
                  <span>
                    {c.billing.status ? `Stripe status ${c.billing.status}` : 'No subscription'}
                    {c.billing.tierCode ? ` · tier ${c.billing.tierCode}` : ''}
                    {c.billing.interval ? ` · ${c.billing.interval}ly` : ''}
                    {c.billing.trialEndsAt && c.billing.plan === 'trial' ? ` · trial ends ${shortDate(c.billing.trialEndsAt)}` : ''}
                    {c.billing.currentPeriodEnd && c.billing.plan === 'paying' ? ` · renews ${shortDate(c.billing.currentPeriodEnd)}` : ''}
                    {c.billing.paymentFailedAt ? ` · payment failed ${shortDate(c.billing.paymentFailedAt)}` : ''}
                  </span>
                </p>
              )}

              <section className="pf-sub">
                <h3 className="pf-sub-title">People active per day <span>last 30 days</span></h3>
                <DayBars data={d.daily} height={90} detail={(x) => `${dayLabel(x.day)} · ${x.people} ${x.people === 1 ? 'person' : 'people'}`} />
              </section>

              <section className="pf-sub">
                <div className="pf-sub-head">
                  <h3 className="pf-sub-title">People <span>{d.people.length}</span></h3>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label className="pf-search pf-search-sm">
                      <Search size={14} />
                      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a person" aria-label="Find a person" />
                    </label>
                    <button className="pf-btn pf-btn-ghost" onClick={exportPeople}><Download size={14} /> CSV</button>
                  </div>
                </div>

                <div className="pf-people">
                  {people.map((p) => (
                    <div key={p.id} className={'pf-person' + (p.status !== 'active' ? ' pf-person-off' : '')}>
                      <span className="pf-avatar">
                        {initials(p.name)}
                        {p.online && <i className="pf-dot pf-dot-on" />}
                      </span>
                      <div className="pf-person-main">
                        <p className="pf-person-name">
                          {p.name}
                          <span className="pf-role">{ROLE_LABEL[p.role] || p.role}</span>
                          {p.status !== 'active' && <span className="pf-role pf-role-off">{p.status}</span>}
                        </p>
                        <p className="pf-person-sub">{p.email}{p.departments ? ` · ${p.departments}` : ''}</p>
                      </div>
                      <div className="pf-person-seen" title={`Last active ${fullDate(p.lastActiveAt)}\nLast typed password ${fullDate(p.lastLoginAt)}`}>
                        <p className={p.online ? 'pf-ok' : ''}>
                          {p.online ? 'On now' : ago(p.lastActiveAt || p.lastLoginAt, now)}
                          {p.lastActiveClient && <span className="pf-client"><ClientIcon client={p.lastActiveClient} /></span>}
                        </p>
                        <span>{p.daysActive30 ? `${p.daysActive30} of 30 days` : (p.lastActiveAt || p.lastLoginAt) ? 'Not in 30 days' : 'Never signed in'}</span>
                      </div>
                    </div>
                  ))}
                  {!people.length && <p className="pf-empty-line">No one matches “{q}”.</p>}
                </div>
              </section>
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
}

// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────
export default function Platform() {
  const [overview, setOverview] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [activity, setActivity] = useState(null);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('active');
  const [openId, setOpenId] = useState(null);
  const [activityTab, setActivityTab] = useState('online');

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setRefreshing(true);
    try {
      const [o, c, a] = await Promise.all([
        apiService.getPlatformOverview({ days: 30 }),
        apiService.getPlatformCompanies(),
        apiService.getPlatformActivity({ hours: 24, limit: 200 }),
      ]);
      setOverview(unwrap(o)); setCompanies(unwrap(c)?.companies || []); setActivity(unwrap(a));
      setError(null); setUpdatedAt(new Date()); setNow(Date.now());
    } catch (e) {
      if ([403, 404].includes(e?.response?.status)) setDenied(true);
      else setError(e?.response?.data?.message || e?.message || 'Could not load the console.');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Keep it live while the tab is open and visible
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(true); }, REFRESH_MS);
    const tick = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => { clearInterval(t); clearInterval(tick); };
  }, [load]);

  const counts = useMemo(() => {
    const list = companies || [];
    const quiet = (c) => !c.lastActiveAt || now - new Date(c.lastActiveAt).getTime() > 30 * 86400000;
    return {
      all: list.length,
      online: list.filter((c) => c.onlineNow > 0).length,
      active7: list.filter((c) => c.active7 > 0).length,
      quiet: list.filter(quiet).length,
      trial: list.filter((c) => c.billing?.plan === 'trial').length,
      paying: list.filter((c) => c.billing?.plan === 'paying').length,
      read_only: list.filter((c) => c.billing?.plan === 'read_only').length,
      suspended: list.filter((c) => !c.isActive).length,
      _quiet: quiet,
    };
  }, [companies, now]);

  const shown = useMemo(() => {
    let list = [...(companies || [])];
    const term = query.trim().toLowerCase();
    if (term) {
      list = list.filter((c) => `${c.name} ${c.owner?.name || ''} ${c.owner?.email || ''} ${c.country || ''} ${c.partner?.name || ''}`.toLowerCase().includes(term));
    }
    if (filter === 'online') list = list.filter((c) => c.onlineNow > 0);
    else if (filter === 'active7') list = list.filter((c) => c.active7 > 0);
    else if (filter === 'quiet') list = list.filter(counts._quiet);
    else if (filter === 'suspended') list = list.filter((c) => !c.isActive);
    else if (filter !== 'all') list = list.filter((c) => c.billing?.plan === filter);
    const t = (v) => (v ? new Date(v).getTime() : 0);
    if (sort === 'active') list.sort((a, b) => (b.onlineNow - a.onlineNow) || (t(b.lastActiveAt) - t(a.lastActiveAt)));
    else if (sort === 'newest') list.sort((a, b) => t(b.createdAt) - t(a.createdAt));
    else if (sort === 'people') list.sort((a, b) => b.activePeople - a.activePeople);
    else list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    return list;
  }, [companies, query, filter, sort, counts]);

  const exportCompanies = () => {
    downloadCsv(`clockout-customers-${format(new Date(), 'yyyy-MM-dd')}.csv`, [
      ['Company', 'Country', 'Plan', 'Billing status', 'Owner', 'Owner email', 'People', 'On now', 'Active 7 days', 'Active 30 days', 'Never signed in', 'Last active', 'Last active by', 'Requests 30 days', 'Requests total', 'Joined', 'Suspended'],
      ...shown.map((c) => [c.name, c.country, PLAN[c.billing?.plan]?.label || '', c.billing?.status || '', c.owner?.name || '', c.owner?.email || '', c.activePeople, c.onlineNow, c.active7, c.active30, c.neverSignedIn, fullDate(c.lastActiveAt), c.lastActiveBy?.name || '', c.requests.last30, c.requests.total, shortDate(c.createdAt), c.isActive ? '' : 'yes']),
    ]);
  };

  const people = activity?.people || [];
  const onlinePeople = people.filter((p) => p.online);
  const activityList = activityTab === 'online' ? onlinePeople : people;

  if (denied) {
    return (
      <div className="pf-denied">
        <ShieldAlert size={26} />
        <h1>Page not found</h1>
        <p>This page isn&apos;t available for your account.</p>
      </div>
    );
  }

  const o = overview;
  const tiles = o ? [
    { icon: Building2, label: 'Companies', value: o.companies.total, sub: `${o.companies.new30} new in 30 days${o.companies.suspended ? ` · ${o.companies.suspended} suspended` : ''}` },
    { icon: Users, label: 'People', value: o.people.active, sub: `${o.people.neverSignedIn} never signed in` },
    { icon: Activity, label: 'On now', value: o.people.online, sub: `in ${o.companies.online} ${o.companies.online === 1 ? 'company' : 'companies'} · last ${o.onlineMinutes} min`, live: true },
    { icon: CalendarCheck, label: 'Active today', value: o.people.activeToday, sub: `${o.companies.activeToday} ${o.companies.activeToday === 1 ? 'company' : 'companies'}` },
    { icon: Users, label: 'Active this week', value: o.people.active7, sub: `${o.companies.active7} companies · ${o.people.active30} in 30 days` },
    { icon: CreditCard, label: 'Plans', value: `${o.plans.paying} paying`, sub: `${o.plans.trial} on trial${o.plans.read_only ? ` · ${o.plans.read_only} read only` : ''}${o.plans.enterprise + o.plans.manual ? ` · ${o.plans.enterprise + o.plans.manual} other` : ''}` },
  ] : [];

  return (
    <div className="animate-fade-in pf" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{CSS}</style>

      {/* Header */}
      <div className="pf-head">
        <div style={{ minWidth: 0 }}>
          <h1 className="pf-title">Customers</h1>
          <p className="pf-subtitle">
            Every company using Clock-out and who&apos;s on
            {updatedAt && <> · updated {format(updatedAt, 'HH:mm')}</>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="pf-btn pf-btn-ghost" onClick={exportCompanies} disabled={!companies?.length}><Download size={15} /> <span className="pf-hide-sm">Export</span></button>
          <button className="pf-btn" onClick={() => load()} disabled={refreshing} aria-label="Refresh">
            <RefreshCw size={15} className={refreshing ? 'pf-spin' : ''} /> <span className="pf-hide-sm">Refresh</span>
          </button>
        </div>
      </div>

      {error && <div className="pf-error"><ShieldAlert size={16} /> {error}</div>}

      {/* Headline figures */}
      <div className="pf-tiles">
        {!o
          ? [0, 1, 2, 3, 4, 5].map((i) => <span key={i} className="pf-skel" style={{ height: 96, borderRadius: 18 }} />)
          : tiles.map(({ icon: Icon, label, value, sub, live }) => (
            <div key={label} className="pf-tile">
              <div className="pf-tile-top">
                <span className="pf-tile-icon"><Icon size={15} /></span>
                <span className="pf-tile-label">{label}</span>
                {live && <i className={'pf-dot' + (o.people.online ? ' pf-dot-on pf-pulse' : '')} />}
              </div>
              <p className="pf-tile-val">{value}</p>
              <p className="pf-tile-sub">{sub}</p>
            </div>
          ))}
      </div>

      {/* Chart + who's on */}
      <div className="pf-row">
        <section className="pf-card">
          <div className="pf-card-head">
            <h2 className="pf-card-title">People active per day</h2>
            <span className="pf-muted">last 30 days</span>
          </div>
          <div style={{ padding: '14px 18px 16px' }}>
            {o
              ? <DayBars data={o.daily} height={190} detail={(d) => `${dayLabel(d.day)} · ${d.people} ${d.people === 1 ? 'person' : 'people'} in ${d.companies} ${d.companies === 1 ? 'company' : 'companies'} · web ${d.web}, app ${d.app}`} />
              : <span className="pf-skel" style={{ height: 160, display: 'block' }} />}
          </div>
        </section>

        <section className="pf-card" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div className="pf-card-head">
            <div className="pf-seg" role="tablist">
              <button role="tab" aria-selected={activityTab === 'online'} className={activityTab === 'online' ? 'on' : ''} onClick={() => setActivityTab('online')}>
                On now <b>{onlinePeople.length}</b>
              </button>
              <button role="tab" aria-selected={activityTab === 'day'} className={activityTab === 'day' ? 'on' : ''} onClick={() => setActivityTab('day')}>
                Last 24 h <b>{people.length}</b>
              </button>
            </div>
          </div>
          <div className="pf-feed">
            {!activity && [0, 1, 2, 3].map((i) => <span key={i} className="pf-skel" style={{ height: 40, margin: '8px 18px', display: 'block' }} />)}
            {activity && activityList.map((p) => (
              <button key={p.id} className="pf-feed-row" onClick={() => setOpenId(p.company.id)} title={`${p.email}\nLast active ${fullDate(p.lastActiveAt)}`}>
                <span className="pf-avatar pf-avatar-sm">{initials(p.name)}{p.online && <i className="pf-dot pf-dot-on" />}</span>
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span className="pf-feed-name">{p.name}</span>
                  <span className="pf-feed-sub">{p.company.name}{p.company.isOwn ? ' (you)' : ''}</span>
                </span>
                <span className="pf-feed-when">
                  <ClientIcon client={p.lastActiveClient} />
                  {p.online ? 'now' : ago(p.lastActiveAt, now)}
                </span>
              </button>
            ))}
            {activity && !activityList.length && (
              <p className="pf-empty-line">{activityTab === 'online' ? `No one in the last ${activity.onlineMinutes} minutes.` : 'No one in the last 24 hours.'}</p>
            )}
          </div>
        </section>
      </div>

      {/* Companies */}
      <section className="pf-card" style={{ overflow: 'hidden' }}>
        <div className="pf-card-head pf-toolbar">
          <h2 className="pf-card-title">Companies <span className="pf-count">{shown.length}{shown.length !== (companies?.length || 0) ? ` of ${companies?.length || 0}` : ''}</span></h2>
          <div className="pf-tools">
            <label className="pf-search">
              <Search size={15} />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Company, owner or email" aria-label="Search companies" />
              {query && <button onClick={() => setQuery('')} aria-label="Clear search"><X size={14} /></button>}
            </label>
            <select className="pf-select" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort by">
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="pf-filters">
          {FILTERS.map((f) => (
            <button key={f.key} className={'pf-filter' + (filter === f.key ? ' on' : '')} onClick={() => setFilter(f.key)}
              disabled={f.key !== 'all' && !counts[f.key] && filter !== f.key}>
              {f.label} <b>{counts[f.key] ?? 0}</b>
            </button>
          ))}
        </div>

        <div className="pf-table" role="table" aria-label="Companies">
          <div className="pf-tr pf-th" role="row">
            <span role="columnheader">Company</span>
            <span role="columnheader">People</span>
            <span role="columnheader">Last active</span>
            <span role="columnheader">Active this week</span>
            <span role="columnheader">Plan</span>
            <span role="columnheader" className="pf-num" title="Leave requests made in the last 30 days">Requests 30d</span>
            <span role="columnheader">Joined</span>
            <span />
          </div>

          {!companies && [0, 1, 2, 3, 4].map((i) => <span key={i} className="pf-skel" style={{ height: 52, margin: '8px 18px', display: 'block' }} />)}

          {companies && shown.map((c) => {
            const weekPct = c.activePeople ? Math.round((c.active7 / c.activePeople) * 100) : 0;
            return (
              <button key={c.id} className={'pf-tr pf-row-btn' + (!c.isActive ? ' pf-row-off' : '')} role="row" onClick={() => setOpenId(c.id)}>
                <span className="pf-co" role="cell">
                  <span className="pf-co-badge">{initials(c.name)}</span>
                  <span style={{ minWidth: 0 }}>
                    <span className="pf-co-name">
                      {c.name}
                      {c.isOwn && <span className="pf-chip pf-chip-sm">You</span>}
                      {!c.isActive && <span className="pf-chip pf-chip-sm pf-chip-bad">Suspended</span>}
                    </span>
                    <span className="pf-co-sub">
                      {c.country || '—'}
                      {c.owner ? ` · ${c.owner.email}` : ''}
                    </span>
                  </span>
                </span>
                <span role="cell" className="pf-cell">
                  <b>{c.activePeople}</b>
                  {c.neverSignedIn > 0 && <span className="pf-cell-sub pf-warn-text"><UserX size={12} /> {c.neverSignedIn} never signed in</span>}
                </span>
                <span role="cell" className="pf-cell">
                  {c.onlineNow > 0
                    ? <span className="pf-online"><i className="pf-dot pf-dot-on" /> {c.onlineNow} on now</span>
                    : <span className={counts._quiet(c) ? 'pf-quiet' : ''}>{ago(c.lastActiveAt, now)}</span>}
                  {c.lastActiveBy && <span className="pf-cell-sub"><ClientIcon client={c.lastActiveBy.client} size={12} /> {c.lastActiveBy.name}</span>}
                </span>
                <span role="cell" className="pf-cell">
                  <span className="pf-meter-wrap">
                    <span className="pf-meter"><span style={{ width: `${weekPct}%` }} /></span>
                    <span className="pf-meter-txt">{c.active7}/{c.activePeople}</span>
                  </span>
                </span>
                <span role="cell" className="pf-cell"><PlanChip billing={c.billing} /></span>
                <span role="cell" className="pf-cell pf-num">
                  <b>{c.requests.last30}</b>
                  <span className="pf-cell-sub">{c.requests.total} total</span>
                </span>
                <span role="cell" className="pf-cell pf-muted">{shortDate(c.createdAt)}</span>
                <ChevronRight size={16} className="pf-go" aria-hidden="true" />
                <span className="pf-mobile-line" aria-hidden="true">
                  <span><b>{c.activePeople}</b> people</span>
                  <span className={c.onlineNow ? 'pf-online' : ''}>{c.onlineNow ? `${c.onlineNow} on now` : `Active ${ago(c.lastActiveAt, now).toLowerCase()}`}</span>
                  <span><b>{c.active7}</b> this week</span>
                  {c.neverSignedIn > 0 && <span className="pf-warn-text">{c.neverSignedIn} never signed in</span>}
                </span>
              </button>
            );
          })}

          {companies && !shown.length && (
            <div className="pf-empty">
              <Inbox size={20} />
              <span>No companies match.</span>
              {(query || filter !== 'all') && <button className="pf-link" onClick={() => { setQuery(''); setFilter('all'); }}>Clear filters</button>}
            </div>
          )}
        </div>
      </section>

      <p className="pf-foot">
        &ldquo;Active&rdquo; means they used the web or mobile app; &ldquo;on now&rdquo; is the last {o?.onlineMinutes ?? 10} minutes. Times are your computer&apos;s time; days are counted in {o?.timezone ?? 'Europe/London'}.
      </p>

      {openId && <CompanyDrawer companyId={openId} now={now} onClose={() => setOpenId(null)} />}
    </div>
  );
}

const CSS = `
  .pf { --pf-on: #1E9E5A; }
  .pf-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding-top: 2px; }
  .pf-title { margin: 0; font-size: 26px; font-weight: 700; letter-spacing: -0.02em; color: var(--text); }
  .pf-subtitle { margin: 4px 0 0; font-size: 13px; color: var(--text-muted); }
  .pf-muted { font-size: 12.5px; color: var(--text-muted); }
  .pf-eyebrow { margin: 0; font-size: 11px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.08em; }

  .pf-btn { height: 40px; padding: 0 14px; display: inline-flex; align-items: center; gap: 7px; border-radius: 12px; border: 1px solid #FFC81E; background: #FFC81E; color: #1C1A14; font: inherit; font-size: 13.5px; font-weight: 600; cursor: pointer; white-space: nowrap; }
  .pf-btn:hover:not(:disabled) { background: #F5A300; border-color: #F5A300; }
  .pf-btn:disabled { opacity: 0.6; cursor: default; }
  .pf-btn-ghost { background: var(--surface); border-color: var(--border); color: var(--text); }
  .pf-btn-ghost:hover:not(:disabled) { background: var(--hover); border-color: var(--border); }
  .pf-btn:focus-visible, .pf-icon-btn:focus-visible, .pf-row-btn:focus-visible, .pf-feed-row:focus-visible, .pf-filter:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
  .pf-icon-btn { width: 36px; height: 36px; border-radius: 10px; border: 1px solid var(--border); background: var(--surface); color: var(--text); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
  .pf-icon-btn:hover { background: var(--hover); }
  .pf-link { border: none; background: none; color: var(--accent); font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; text-decoration: underline; }
  @keyframes pfSpin { to { transform: rotate(360deg); } }
  .pf-spin { animation: pfSpin 0.9s linear infinite; }

  .pf-error { display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 12px; background: var(--bad-bg); border: 1px solid var(--bad-border); color: var(--bad-text); font-size: 13px; }

  .pf-tiles { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 12px; }
  @media (max-width: 1365px) { .pf-tiles { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
  @media (max-width: 639px)  { .pf-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; } }
  .pf-tile { background: var(--surface); border: 1px solid var(--border); border-radius: 18px; padding: 14px 16px; box-shadow: var(--shadow); min-width: 0; }
  .pf-tile-top { display: flex; align-items: center; gap: 8px; }
  .pf-tile-icon { width: 26px; height: 26px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; background: var(--tint); color: var(--accent); flex-shrink: 0; }
  .pf-tile-label { flex: 1; min-width: 0; font-size: 12.5px; font-weight: 600; color: var(--text-2, var(--text)); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pf-tile-val { margin: 10px 0 0; font-family: 'Rubik', 'DM Sans', sans-serif; font-size: 26px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.1; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pf-tile-sub { margin: 4px 0 0; font-size: 11.5px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  .pf-dot { position: relative; display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: var(--text-faint); flex-shrink: 0; }
  .pf-dot-on { background: var(--pf-on); }
  @keyframes pfPulse { 0% { box-shadow: 0 0 0 0 rgba(30,158,90,0.45); } 70% { box-shadow: 0 0 0 7px rgba(30,158,90,0); } 100% { box-shadow: 0 0 0 0 rgba(30,158,90,0); } }
  .pf-pulse { animation: pfPulse 1.8s ease-out infinite; }
  .pf-ok { color: var(--ok-text) !important; }
  .pf-warn { color: var(--warn-text) !important; }
  .pf-warn-text { color: var(--warn-text); }

  .pf-row { display: grid; grid-template-columns: minmax(0, 1.7fr) minmax(280px, 1fr); gap: 16px; }
  @media (max-width: 1023px) { .pf-row { grid-template-columns: 1fr; } }
  .pf-card { background: var(--surface); border: 1px solid var(--border); border-radius: 24px; box-shadow: var(--shadow); min-width: 0; }
  .pf-card-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 14px 18px; border-bottom: 1px solid var(--border-soft); min-height: 58px; box-sizing: border-box; }
  .pf-card-title { margin: 0; font-size: 15px; font-weight: 600; color: var(--text); display: flex; align-items: center; gap: 8px; }
  .pf-count { font-size: 12px; font-weight: 700; color: var(--text-2, var(--text)); background: var(--border); border-radius: 10px; padding: 1px 8px; }

  .pf-bars { display: flex; align-items: stretch; gap: 3px; }
  .pf-bar-col { flex: 1; min-width: 0; display: flex; cursor: default; }
  .pf-bar-plot { flex: 1; display: flex; align-items: flex-end; justify-content: center; border-bottom: 1px solid var(--border); }
  .pf-bar { width: 100%; max-width: 22px; border-radius: 5px 5px 0 0; transition: height 0.35s, opacity 0.15s; }
  .pf-bar-zero { width: 100%; max-width: 22px; height: 2px; border-radius: 1px; background: var(--border); }
  .pf-bars-foot { display: flex; justify-content: space-between; gap: 10px; margin-top: 8px; font-size: 11.5px; color: var(--text-muted); }
  .pf-bars-hover { flex: 1; text-align: center; color: var(--text-2, var(--text)); font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  .pf-seg { display: inline-flex; padding: 3px; border-radius: 11px; background: var(--tint); gap: 2px; }
  .pf-seg button { border: none; background: transparent; padding: 6px 12px; border-radius: 9px; font: inherit; font-size: 12.5px; font-weight: 600; color: var(--text-muted); cursor: pointer; display: inline-flex; gap: 6px; align-items: center; }
  .pf-seg button.on { background: var(--surface); color: var(--text); box-shadow: 0 1px 2px rgba(0,0,0,0.06); }
  .pf-seg b { font-weight: 700; font-size: 11.5px; }
  .pf-feed { flex: 1; overflow-y: auto; max-height: 236px; padding: 6px 0; }
  .pf-feed-row { display: flex; align-items: center; gap: 10px; width: 100%; padding: 7px 18px; border: none; background: transparent; font: inherit; text-align: left; cursor: pointer; color: inherit; }
  .pf-feed-row:hover { background: var(--hover); }
  .pf-feed-name { display: block; font-size: 13px; font-weight: 600; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pf-feed-sub { display: block; font-size: 12px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pf-feed-when { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; color: var(--text-muted); white-space: nowrap; flex-shrink: 0; }

  .pf-avatar { position: relative; width: 34px; height: 34px; border-radius: 50%; background: var(--avatar-bg); color: var(--avatar-text); font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .pf-avatar-sm { width: 30px; height: 30px; font-size: 11px; }
  .pf-avatar .pf-dot { position: absolute; right: -1px; bottom: -1px; width: 10px; height: 10px; border: 2px solid var(--surface); }

  .pf-toolbar { flex-wrap: wrap; }
  .pf-tools { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .pf-search { display: inline-flex; align-items: center; gap: 7px; height: 38px; padding: 0 10px; border-radius: 11px; border: 1px solid var(--field-border, var(--border)); background: var(--field, var(--surface)); color: var(--text-muted); width: 260px; max-width: 100%; box-sizing: border-box; }
  .pf-search:focus-within { border-color: var(--accent); }
  .pf-search input { flex: 1; min-width: 0; border: none; outline: none; background: transparent; color: var(--text); font: inherit; font-size: 13px; }
  .pf-search button { border: none; background: none; color: var(--text-muted); cursor: pointer; display: inline-flex; padding: 2px; }
  .pf-search-sm { height: 34px; width: 200px; }
  .pf-select { height: 38px; padding: 0 30px 0 12px; border-radius: 11px; border: 1px solid var(--field-border, var(--border)); background: var(--field, var(--surface)); color: var(--text); font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }

  .pf-filters { display: flex; gap: 6px; padding: 10px 18px; border-bottom: 1px solid var(--border-soft); overflow-x: auto; scrollbar-width: thin; }
  .pf-filter { flex-shrink: 0; height: 30px; padding: 0 11px; border-radius: 15px; border: 1px solid var(--border); background: var(--surface); color: var(--text-2, var(--text)); font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
  .pf-filter b { font-size: 11px; color: var(--text-muted); }
  .pf-filter:hover:not(:disabled) { background: var(--hover); }
  .pf-filter.on { background: #FFC81E; border-color: #FFC81E; color: #1C1A14; }
  .pf-filter.on b { color: #1C1A14; }
  .pf-filter:disabled { opacity: 0.45; cursor: default; }

  .pf-table { display: flex; flex-direction: column; }
  .pf-tr { display: grid; grid-template-columns: minmax(220px, 2.2fr) minmax(90px, 0.9fr) minmax(140px, 1.3fr) minmax(110px, 1fr) minmax(110px, 1fr) minmax(80px, 0.7fr) minmax(90px, 0.8fr) 18px; gap: 14px; align-items: center; padding: 11px 18px; }
  .pf-th { font-size: 11px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.06em; padding-top: 10px; padding-bottom: 10px; border-bottom: 1px solid var(--border-soft); }
  .pf-th > span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pf-row-btn { width: 100%; border: none; background: transparent; font: inherit; color: inherit; text-align: left; cursor: pointer; border-bottom: 1px solid var(--border-soft); }
  .pf-row-btn:last-child { border-bottom: none; }
  .pf-row-btn:hover { background: var(--hover); }
  .pf-row-btn:hover .pf-go { color: var(--text-2, var(--text)); transform: translateX(2px); }
  .pf-row-off { opacity: 0.65; }
  .pf-go { color: var(--text-faint); transition: transform 0.15s, color 0.15s; }
  .pf-num { text-align: right; justify-self: end; align-items: flex-end; }
  .pf-co { display: flex; align-items: center; gap: 11px; min-width: 0; }
  .pf-co-badge { width: 36px; height: 36px; border-radius: 11px; background: var(--tint); color: var(--text); font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .pf-co-name { display: flex; align-items: center; gap: 6px; font-size: 13.5px; font-weight: 600; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pf-co-sub { display: block; margin-top: 2px; font-size: 12px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pf-cell { display: flex; flex-direction: column; gap: 2px; min-width: 0; font-size: 13px; color: var(--text); }
  .pf-cell b { font-weight: 700; }
  .pf-cell-sub { display: inline-flex; align-items: center; gap: 4px; font-size: 11.5px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pf-online { display: inline-flex; align-items: center; gap: 6px; color: var(--ok-text); font-weight: 600; }
  .pf-quiet { color: var(--bad-text); }
  .pf-meter-wrap { display: flex; align-items: center; gap: 8px; }
  .pf-meter { flex: 1; max-width: 70px; height: 6px; border-radius: 3px; background: var(--track); overflow: hidden; }
  .pf-meter span { display: block; height: 100%; background: var(--chart-past); border-radius: 3px; }
  .pf-meter-txt { font-size: 12px; color: var(--text-2, var(--text)); font-weight: 600; }

  .pf-chip { display: inline-flex; align-items: center; height: 22px; padding: 0 9px; border-radius: 11px; border: 1px solid var(--border); background: var(--tint); color: var(--text); font-size: 11.5px; font-weight: 600; white-space: nowrap; width: fit-content; }
  .pf-chip-sm { height: 18px; padding: 0 7px; font-size: 10.5px; }
  .pf-chip-bad { background: var(--bad-bg); color: var(--bad-text); border-color: var(--bad-border); }

  .pf-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; margin: 18px; padding: 26px 12px; border: 1.5px dashed var(--border); border-radius: 16px; color: var(--text-muted); font-size: 13px; }
  .pf-empty-line { margin: 0; padding: 18px; text-align: center; font-size: 13px; color: var(--text-muted); }
  .pf-foot { display: flex; align-items: center; gap: 6px; margin: 0; font-size: 12px; color: var(--text-muted); }

  @keyframes pfShimmer { 0% { background-position: -200px 0; } 100% { background-position: 200px 0; } }
  .pf-skel { display: block; background: linear-gradient(90deg, var(--skel-a) 0, var(--skel-b) 50%, var(--skel-a) 100%); background-size: 400px 100%; animation: pfShimmer 1.2s linear infinite; border-radius: 10px; }

  .pf-denied { display: flex; flex-direction: column; align-items: center; gap: 6px; margin: 80px auto; max-width: 360px; text-align: center; color: var(--text-muted); }
  .pf-denied h1 { margin: 6px 0 0; font-size: 20px; color: var(--text); }
  .pf-denied p { margin: 0; font-size: 13.5px; }

  /* Drawer */
  .pf-overlay { position: fixed; inset: 0; z-index: 1000; background: var(--overlay); display: flex; justify-content: flex-end; animation: pfFade 0.15s ease-out; }
  @keyframes pfFade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes pfSlide { from { transform: translateX(24px); opacity: 0.6; } to { transform: none; opacity: 1; } }
  .pf-drawer { width: min(760px, 100vw); height: 100%; background: var(--bg); color: var(--text); box-shadow: var(--modal-shadow); display: flex; flex-direction: column; animation: pfSlide 0.2s ease-out; }
  .pf-drawer-head { display: flex; align-items: flex-start; gap: 12px; padding: 20px 22px 16px; background: var(--surface); border-bottom: 1px solid var(--border); }
  .pf-drawer-title { margin: 3px 0 0; font-size: 21px; font-weight: 700; letter-spacing: -0.01em; color: var(--text); overflow-wrap: anywhere; }
  .pf-drawer-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 12px; margin-top: 10px; font-size: 12.5px; color: var(--text-muted); }
  .pf-drawer-body { flex: 1; overflow-y: auto; padding: 18px 22px 28px; display: flex; flex-direction: column; gap: 16px; }
  .pf-mini-stats { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 8px; }
  @media (max-width: 719px) { .pf-mini-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
  .pf-mini-stats > div { background: var(--surface); border: 1px solid var(--border); border-radius: 14px; padding: 10px 12px; min-width: 0; }
  .pf-mini-stats b { display: block; font-family: 'Rubik', 'DM Sans', sans-serif; font-size: 20px; font-weight: 600; color: var(--text); }
  .pf-mini-stats span { display: block; margin-top: 2px; font-size: 11.5px; color: var(--text-muted); line-height: 1.25; }
  .pf-note { display: flex; align-items: center; gap: 8px; margin: 0; padding: 9px 12px; border-radius: 12px; background: var(--surface); border: 1px solid var(--border); font-size: 12.5px; color: var(--text-2, var(--text)); }
  .pf-note svg { flex-shrink: 0; color: var(--text-muted); }
  .pf-sub { background: var(--surface); border: 1px solid var(--border); border-radius: 18px; padding: 14px 16px; }
  .pf-sub-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 8px; }
  .pf-sub-title { margin: 0 0 10px; font-size: 14px; font-weight: 600; color: var(--text); display: flex; align-items: baseline; gap: 8px; }
  .pf-sub-head .pf-sub-title { margin: 0; }
  .pf-sub-title span { font-size: 12px; font-weight: 500; color: var(--text-muted); }
  .pf-people { display: flex; flex-direction: column; margin: 0 -16px -14px; }
  .pf-person { display: flex; align-items: center; gap: 11px; padding: 10px 16px; border-top: 1px solid var(--border-soft); }
  .pf-person-off { opacity: 0.6; }
  .pf-person-main { flex: 1; min-width: 0; }
  .pf-person-name { margin: 0; display: flex; align-items: center; gap: 6px; font-size: 13.5px; font-weight: 600; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pf-person-sub { margin: 2px 0 0; font-size: 12px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pf-role { font-size: 10.5px; font-weight: 700; padding: 1px 7px; border-radius: 8px; background: var(--tint); color: var(--text-2, var(--text)); text-transform: capitalize; flex-shrink: 0; }
  .pf-role-off { background: var(--bad-bg); color: var(--bad-text); }
  .pf-person-seen { text-align: right; flex-shrink: 0; }
  .pf-person-seen p { margin: 0; display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 600; color: var(--text); }
  .pf-person-seen > span { display: block; margin-top: 2px; font-size: 11.5px; color: var(--text-muted); }
  .pf-client { display: inline-flex; color: var(--text-muted); }

  /* Narrower screens: the table becomes stacked cards */
  @media (max-width: 1100px) {
    .pf-th { display: none; }
    .pf-tr { grid-template-columns: minmax(0, 1fr) auto; gap: 6px 12px; padding: 13px 16px; }
    .pf-co { grid-column: 1 / 2; }
    .pf-tr > .pf-cell:nth-child(5) { grid-column: 2; grid-row: 1; justify-self: end; }
    .pf-tr > .pf-cell:nth-child(2), .pf-tr > .pf-cell:nth-child(3), .pf-tr > .pf-cell:nth-child(4), .pf-tr > .pf-cell:nth-child(6), .pf-tr > .pf-cell:nth-child(7) { display: none; }
    .pf-tr .pf-go { display: none; }
  }
  .pf-mobile-line { display: none; }
  @media (max-width: 1100px) {
    .pf-mobile-line { display: flex; grid-column: 1 / -1; flex-wrap: wrap; gap: 4px 14px; font-size: 12px; color: var(--text-muted); padding-left: 47px; }
    .pf-mobile-line b { color: var(--text); font-weight: 600; }
  }
  @media (max-width: 639px) {
    .pf-title { font-size: 22px; }
    .pf-hide-sm { display: none; }
    .pf-btn { padding: 0 11px; }
    .pf-search { width: 100%; }
    .pf-tools { width: 100%; }
    .pf-tools .pf-search { flex: 1; width: auto; }
    .pf-person-seen > span { display: none; }
    .pf-drawer-body { padding: 14px 14px 24px; }
    .pf-drawer-head { padding: 16px 14px 12px; }
    .pf-search-sm { width: 100%; }
  }
`;